<template>
    <div class="modal-overlay">
        <transition name="modal-expand">
            <div v-if="isVisible" class="products-modal" @click.stop>
            <div class="modal-content" @click.stop> <!-- Stop the click event from propagating to the overlay -->
                <font-awesome-icon class="window-close-icon" :icon="['fas', 'window-close']" @click="close" />
                <h2>{{ isEdit ? 'Edit Chapter' : 'Add New Chapter' }}</h2>

                    <div class="add">
                        <form @submit.prevent="submit">
                            <div class="form-group">
                                <label for="chapt-no">ATA No:</label>
                                <input type="text" class="form-control rounded" id="chapt-no" name="chapt-no"
                                    v-model="form.chapt_no" />
                            </div>
                            <div class="form-group">
                                <label for="chapter-name">Name:</label>
                                <input class="form-control rounded" v-model="form.name" type="text"
                                    placeholder="Chapter Name" />
                            </div>
                            <div v-if="showAlert" class="alert alert-danger error-message" role="alert">
                                <div v-if="errorMessage">{{ errorMessage }}</div>
                            </div>
                            <div>
                                <button class="saveButton" type="submit" style="margin-top: 10px;">{{ isEdit ? 'Update Chapter' : 'Save' }}</button>
                            </div>
                        </form>
                    </div>
            </div>
        </div>
        </transition>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    props: {
        chapters: Array,
        chapter: { type: Object, default: null }, // null = Add mode
        mode: { type: String, default: "add" }
    },
    data() {
        return {
            form: { 
                chapt_no: "",
                name: "",
             },
            showAlert: false,
            errorMessage: "",
            isVisible: false,
        };
    },
    computed: {
        isEdit() {
            return this.mode === "edit";
        },
    },
    methods: {
        close() {
            this.$emit('close');
        },
        async submit() {
            if (!this.form.name.trim()) {
                this.errorMessage = "Chapter name is required";
                this.showAlert = true;
                return;
            }

            const name = this.form.name.trim().toLowerCase();
            const chapt_no = this.form.chapt_no.trim().toLowerCase();

            // Duplicate check (ignore self in edit mode)
            const isDuplicate = this.chapters.some(s =>
                s.name.trim().toLowerCase() === name &&
                (!this.isEdit || s.chapter_id !== this.chapter?.chapter_id)
            );

            const isNumberDuplicate = this.chapters.some(s =>
                s.chapt_no.trim().toLowerCase() === chapt_no &&
                (!this.isEdit || s.chapter_id !== this.chapter?.chapter_id)
            );

            if (isDuplicate) {
                window.alert("Chapter name already exists");
                return;
            }

            if (isNumberDuplicate) {
                window.alert("Chapter number already exists");
                return;
            }

            try {
                const token = localStorage.getItem("token");
                const headers = { Authorization: `Bearer ${token}` };

                let response;

                if (this.isEdit) {
                    // PUT update
                    response = await axios.put(`/api/chapters/${this.chapter.chapter_id}`, this.form, { headers });
                } else {
                    // POST add
                    response = await axios.post(`/api/chapters`, this.form, { headers });
                }

                if (response.status === 200 || response.status === 201) {
                    window.alert(`Chapter ${this.isEdit ? "updated" : "added"} successfully`);
                    this.$emit("saved");     // notify parent to refresh
                    this.close();
                }
            } catch (error) {
                if (error.isOffline === true) {
                    this.$emit('close');
                    return;
                }
                console.error('Error updating Chapter:', error.response);
                if (error.response && error.response.data && error.response.data.errors) {
                    // Handle validation errors
                    const validationErrors = error.response.data.errors;

                    // Build an error message from the validation errors
                    let errorMessage = 'Validation errors:';
                    for (const field in validationErrors) {
                        errorMessage += `\n${field}: ${validationErrors[field][0]}`;
                    }

                    this.errorMessage = errorMessage;
                } else {
                    // Handle other errors
                    this.errorMessage = 'An error occurred while updating the Chapter.' + error.response.data.message;
                }
                this.showAlert = true;
            }
        },
    },       
    mounted() {
        this.isVisible = true;
        // Pre-fill fields for editing
        if (this.isEdit && this.chapter) {
            this.form.name = this.chapter.name;
            this.form.chapt_no = this.chapter.chapt_no;
        }
    },
};

</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
}

.products-modal {
    background-color: #fff;
    max-width: 90%;
    min-width: min(600px, 75%);
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1001;
}

.modal-content {
    border: white;
    padding: 15px;
    border: 1px solid #dddddd;
    border-radius: 3px;
}

.saveButton {
    background-color: #0055b1;
    color: #ffffff;
    font-size: 14px;
    padding: 10px;
    border: 1px solid #0a407a;
    border-radius: 4px;
    cursor: pointer;
    margin-bottom: auto;
    font-family: Verdana, Geneva, Tahoma, sans-serif;
    width: 180px;
    margin-left: 30px;
}

.saveButton:hover {
    background-color: rgb(1, 40, 82);
}

h2 {
    font-size: 30px;
    margin-bottom: 20px;
    margin-left: 25px;
}

.alert-danger {
    color: #ff0019;
    background-color: #f8d7da;
    border: 1px solid #e6b0b6;
}

.alert {
    position: relative;
    padding: .75rem 1.25rem;
    margin-bottom: 0rem;
    border: 1px solid transparent;
    border-radius: .25rem;
}

.form-group {
    display: flex;
    align-items: top;
    margin-bottom: 20px;
}

.form-group label {
    width: 110px;
    margin-right: 30px;
    text-align: right;
    margin-bottom: 0px;
    margin-top: 9px;
}

.form-group input.form-control,
.form-group select.form-control {
    flex: 1;
    width: auto;
    margin-left: 0;
    margin-bottom: 0px;
    height: 40px;
}

#chapt-no {
  flex: 0 0 80px;
  /* don't grow, don't shrink, fixed 100px */
  width: 80px;
}

</style>